import { useState } from "react";
import { EmbedPage, SITE, Stat } from "./EmbedShell";
import { computeCityPrices, formatMoney, type Country } from "@/lib/gold-data";

function Cta({ href, children }: { href: string; children: string }) {
  return (
    <a className="dh-cta" href={`${SITE}${href}`} target="_blank" rel="noopener">
      {children}
    </a>
  );
}

function Ghost({ href, children }: { href: string; children: string }) {
  return (
    <a className="dh-ghost" href={`${SITE}${href}`} target="_blank" rel="noopener">
      {children}
    </a>
  );
}

export function GoldCalcEmbed({
  country,
  spotUsdPerOz,
  fx,
  target,
}: {
  country: Country;
  spotUsdPerOz: number;
  fx: number;
  target: string;
}) {
  const p = computeCityPrices({ spotUsdPerOz, fxRate: fx, country, cityPremium: 1 });
  const currency = country === "pakistan" ? "PKR" : "INR";
  const label = country === "pakistan" ? "Pakistan" : "India";
  const [w, setW] = useState("10");
  const [k, setK] = useState("22");
  const [making, setMaking] = useState("8");
  const base = p.perGram24 * (Number(k) / 24) * (Number(w) || 0);
  const total = base * (1 + (Number(making) || 0) / 100);
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <span className="dh-live">
          <span className="dh-dot" /> Live rates
        </span>
        <h1 className="dh-h1">Today Gold Rate Calculator — {label}</h1>
        <div className="dh-grid">
          <Stat k="24K / gram" v={formatMoney(p.perGram24, currency)} />
          <Stat k="22K / gram" v={formatMoney(p.perGram22, currency)} />
        </div>
      </div>
      <div className="dh-card">
        <div className="dh-grid">
          <div>
            <label>Weight (grams)</label>
            <input type="number" min="0" value={w} onChange={(e) => setW(e.target.value)} />
          </div>
          <div>
            <label>Purity</label>
            <select value={k} onChange={(e) => setK(e.target.value)}>
              <option value="24">24K</option>
              <option value="22">22K</option>
              <option value="21">21K</option>
              <option value="18">18K</option>
            </select>
          </div>
          <div>
            <label>Making charges %</label>
            <input type="number" min="0" value={making} onChange={(e) => setMaking(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <div className="dh-k">Metal value</div>
          <div className="dh-v">{formatMoney(base, currency)}</div>
          <div className="dh-k" style={{ marginTop: 8 }}>Total with making</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: "#e8c14a" }}>{formatMoney(total, currency)}</div>
        </div>
        <Cta href={target}>Open the full calculator (city rates, tola, resale) →</Cta>
        <Ghost href={country === "pakistan" ? "/pakistan/cities" : "/india/cities"}>
          City-wise gold rate dekhein →
        </Ghost>
      </div>
    </EmbedPage>
  );
}

export function AgeCalcEmbed({ target }: { target: string }) {
  const [dob, setDob] = useState("2000-01-01");
  const d = new Date(dob);
  const now = new Date();
  const valid = !Number.isNaN(d.getTime()) && d <= now;
  let y = 0, m = 0, days = 0, totalDays = 0;
  if (valid) {
    y = now.getFullYear() - d.getFullYear();
    m = now.getMonth() - d.getMonth();
    days = now.getDate() - d.getDate();
    if (days < 0) { m -= 1; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
    if (m < 0) { y -= 1; m += 12; }
    totalDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  }
  const next = valid ? nextBirthdayDays(d, now) : 0;
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <h1 className="dh-h1">Age Calculator</h1>
        <label>Date of birth</label>
        <input type="date" value={dob} max={now.toISOString().slice(0, 10)} onChange={(e) => setDob(e.target.value)} />
      </div>
      <div className="dh-card">
        <div className="dh-grid">
          <Stat k="Years" v={String(y)} />
          <Stat k="Months" v={String(m)} />
          <Stat k="Days" v={String(days)} />
          <Stat k="Total days" v={totalDays.toLocaleString()} />
          <Stat k="Total hours" v={(totalDays * 24).toLocaleString()} />
          <Stat k="Next birthday in" v={`${next} days`} />
        </div>
        <Cta href={target}>Full age calculator — zodiac, numerology, life progress →</Cta>
        <Ghost href="/age-calculator/gestational-pregnancy">Pregnancy &amp; gestational age calculator →</Ghost>
      </div>
    </EmbedPage>
  );
}

function nextBirthdayDays(dob: Date, now: Date): number {
  const next = new Date(now.getFullYear(), dob.getMonth(), dob.getDate());
  if (next < now) next.setFullYear(now.getFullYear() + 1);
  return Math.ceil((next.getTime() - now.getTime()) / 86400000);
}

export function PregnancyEmbed({ target }: { target: string }) {
  const [lmp, setLmp] = useState(new Date(Date.now() - 60 * 86400000).toISOString().slice(0, 10));
  const d = new Date(lmp);
  const now = new Date();
  const ok = !Number.isNaN(d.getTime()) && d <= now;
  const days = ok ? Math.floor((now.getTime() - d.getTime()) / 86400000) : 0;
  const weeks = Math.floor(days / 7);
  const due = ok ? new Date(d.getTime() + 280 * 86400000) : now;
  const trimester = weeks < 13 ? "First" : weeks < 28 ? "Second" : "Third";
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <h1 className="dh-h1">Pregnancy Due Date &amp; Gestational Age Calculator</h1>
        <label>First day of last menstrual period (LMP)</label>
        <input type="date" value={lmp} max={now.toISOString().slice(0, 10)} onChange={(e) => setLmp(e.target.value)} />
      </div>
      <div className="dh-card">
        <div className="dh-grid">
          <Stat k="Gestational age" v={`${weeks}w ${days % 7}d`} />
          <Stat k="Trimester" v={trimester} />
          <Stat k="Estimated due date" v={due.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} />
          <Stat k="Days to go" v={String(Math.max(0, 280 - days))} />
        </div>
        <Cta href={target}>Week-by-week baby development &amp; full tracker →</Cta>
        <Ghost href={`/pregnancy-calculator-week-${Math.min(42, Math.max(1, weeks || 1))}`}>
          Read what happens in your current pregnancy week →
        </Ghost>
      </div>
    </EmbedPage>
  );
}

export function BmiEmbed({ target }: { target: string }) {
  const [cm, setCm] = useState("170");
  const [kg, setKg] = useState("70");
  const h = (Number(cm) || 0) / 100;
  const bmi = h > 0 ? (Number(kg) || 0) / (h * h) : 0;
  const cat = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Normal" : bmi < 30 ? "Overweight" : "Obese";
  const lo = h > 0 ? 18.5 * h * h : 0;
  const hi = h > 0 ? 24.9 * h * h : 0;
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <h1 className="dh-h1">BMI Calculator</h1>
        <div className="dh-grid">
          <div>
            <label>Height (cm)</label>
            <input type="number" min="0" value={cm} onChange={(e) => setCm(e.target.value)} />
          </div>
          <div>
            <label>Weight (kg)</label>
            <input type="number" min="0" value={kg} onChange={(e) => setKg(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="dh-card">
        <div className="dh-grid">
          <Stat k="Your BMI" v={bmi ? bmi.toFixed(1) : "—"} />
          <Stat k="Category" v={cat} />
          <Stat k="Healthy weight" v={`${lo.toFixed(0)}–${hi.toFixed(0)} kg`} />
        </div>
        <Cta href={target}>Full BMI calculator with age &amp; gender charts →</Cta>
        <Ghost href="/calculators">Explore 100+ free calculators →</Ghost>
      </div>
    </EmbedPage>
  );
}

export function ZakatEmbed({
  goldPerGram24,
  silverPerGram,
  currency,
  target,
}: {
  goldPerGram24: number;
  silverPerGram: number;
  currency: "PKR" | "INR";
  target: string;
}) {
  const [gold, setGold] = useState("50");
  const [silver, setSilver] = useState("0");
  const [cash, setCash] = useState("0");
  const total =
    goldPerGram24 * (Number(gold) || 0) + silverPerGram * (Number(silver) || 0) + (Number(cash) || 0);
  const zakat = total * 0.025;
  const nisab = silverPerGram * 612.36;
  return (
    <EmbedPage target={target}>
      <div className="dh-card">
        <h1 className="dh-h1">Zakat Calculator (Live Gold &amp; Silver Rates)</h1>
        <div className="dh-grid">
          <div>
            <label>Gold (grams, 24K)</label>
            <input type="number" min="0" value={gold} onChange={(e) => setGold(e.target.value)} />
          </div>
          <div>
            <label>Silver (grams)</label>
            <input type="number" min="0" value={silver} onChange={(e) => setSilver(e.target.value)} />
          </div>
          <div>
            <label>Cash &amp; savings</label>
            <input type="number" min="0" value={cash} onChange={(e) => setCash(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="dh-card">
        <div className="dh-grid">
          <Stat k="Total assets" v={formatMoney(total, currency)} />
          <Stat k="Zakat (2.5%)" v={formatMoney(zakat, currency)} />
          <Stat k="Nisab (silver)" v={formatMoney(nisab, currency)} />
        </div>
        <div style={{ fontSize: 13, color: total >= nisab ? "#7be495" : "#ffb4b4", marginTop: 10 }}>
          {total >= nisab ? "Aap nisab se upar hain — zakat wajib hai." : "Aap nisab se neeche hain."}
        </div>
        <Cta href={target}>Full zakat calculator (business, loans, gold karats) →</Cta>
        <Ghost href="/pakistan">Aaj ka live gold rate dekhein →</Ghost>
      </div>
    </EmbedPage>
  );
}
